import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Chip,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Grid,
  Avatar,
  IconButton,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import HistoryIcon from '@mui/icons-material/History';
import DescriptionIcon from '@mui/icons-material/Description';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import API_BASE_URL from '../config/api';
import Whiteboard from './Whiteboard';

const QuestionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const chatEndRef = useRef(null);
  const { user } = useAuth();

  const [question, setQuestion] = useState(null);
  const [answer, setAnswer] = useState(''); // For free response or work/context
  const [selectedOption, setSelectedOption] = useState(null); // For multiple choice
  const [originalAnswer, setOriginalAnswer] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [submissionId, setSubmissionId] = useState(null);
  const [error, setError] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [submissions, setSubmissions] = useState([]);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  const [isViewingPastSubmission, setIsViewingPastSubmission] = useState(false);
  const [loadingPhrase, setLoadingPhrase] = useState('');
  const usedPhrasesRef = useRef(new Set());
  const [audioUrl, setAudioUrl] = useState(null);
  const [loadingAudio, setLoadingAudio] = useState(false);
  const [whiteboardHistory, setWhiteboardHistory] = useState([]);
  const [loadingWhiteboard, setLoadingWhiteboard] = useState(false);

  // Realtime audio interview state (WebRTC)
  const [audioMode, setAudioMode] = useState('text'); // 'text' or 'audio'
  const [isConnected, setIsConnected] = useState(false);
  const [isGradingAudioInterview, setIsGradingAudioInterview] = useState(false);
  const pcRef = useRef(null); // RTCPeerConnection
  const dataChannelRef = useRef(null); // Data channel for events
  const remoteAudioRef = useRef(null); // Audio element for AI responses
  const mediaStreamRef = useRef(null); // Local microphone stream
  const liveInterviewIdRef = useRef(null);
  const transcriptBufferRef = useRef([]);
  const transcriptFlushTimerRef = useRef(null);
  const transcriptFlushPromiseRef = useRef(null);
  const isProcessingAnswer = submitting || isGradingAudioInterview;
  const aiOutputBufferRef = useRef({});
  // Audio recording refs
  const audioContextRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const mixedStreamRef = useRef(null);
  const recordedChunksRef = useRef([]);

  const loadingPhrases = [
    "AI is lowkey judging you...",
    "Polishing ones and zeros for you.",
    "Arguing with the algorithm, brb.",
    "Downloading extra sarcasm...",
    "Teaching the AI not to overshare.",
    "Making things up slightly slower than usual.",
    "Asking other AIs what they would do.",
    "Running on coffee and compute credits.",
    "Consulting the vibes oracle...",
    "Upgrading your request from 'huh?' to 'okay, maybe.'",

    "Pretending this takes effort.",
    "Googling... I mean, thinking very hard.",
    "Rephrasing your question so I finally understand it.",
    "Rolling a D20 for your outcome.",
    "Calibrating response: 10% helpful, 90% dramatic.",
    "Rendering your main character moment...",
    "Checking if this is above my pay grade.",
    "Searching your past decisions for context. Yikes.",
    "Turning your chaos into bullet points.",
    "Buffering... like your social battery at parties.",

    "Compressing your expectations...",
    "Making sure this doesn’t get me cancelled.",
    "Upgrading response from 'bro science' to 'actual science.'",
    "Loading... because instant answers are for NPCs.",
    "Double-checking with Future You. They disapprove.",
    "Adding 3 extra buzzwords so it sounds legit.",
    "Trying not to gaslight you and be helpful.",
    "Counting how many times you’ve asked this at 2am.",
    "Rebooting my last 3 brain cells.",
    "Doing the thing. The CPU-intensive thing.",

    "Summoning the answer from the cloud (not the weather kind).",
    "Encrypting, decrypting, regretting.",
    "Telling your tabs to stop judging you.",
    "Simulating 1,000 scenarios where this goes wrong.",
    "Loading... like your motivation on a Monday.",
    "Checking if this requires math. Please no.",
    "Spinning up a tiny digital hamster to run the wheel.",
    "Trying to remember where I put that function.",
    "Auto-correcting your life choices...",
    "Briefly questioning my existence, then continuing.",

    "Pretending this isn’t just fancy autocomplete.",
    "Casting 'Enhance' on your request.",
    "Parsing: Is this a cry for help or just curiosity?",
    "Calling tech support for my own brain.",
    "Aligning with your intentions... and my legal constraints.",


    "Checking if you remembered to show your work...",
    "Applying partial credit using mysterious exam committee logic...",
    "Verifying you used the right mortality table and not vibes...",
    "Discounting your solution back to time 0 for marking purposes...",
    "Checking if you used i, d, or whatever was convenient...",
    "Running a sensitivity test on how wrong this could be...",
    "Seeing if you solved it or just guessed the most exam-y formula...",
    "Allocating credibility between your answer and the model solution...",
    "Projecting the impact of a small algebra mistake on your soul...",

    "Checking if you assumed life annuity when it clearly said due...",
    "Scanning for 'I assume...' to see what you tried to get away with...",
    "Triangulating your method: brute force, formula, or dark magic...",
    "Comparing your answer to 10,000 past attempts. It’s… unique.",
    "Determining if your sign error is 'minor' or 'oops everything's wrong'...",
    "Running a reasonableness check: negative reserves are usually bad...",
    "Checking whether you converted the rate or just hoped it matched...",
    "Scanning for that one missing step that costs 3 points...",
    "Estimating the variance of your answer across parallel universes...",
    "Checking consistency between your words, math, and actual numbers...",

    "Testing if your final answer matches the units. Yes, units matter...",
    "Seeing if you used the table provided instead of your favorite one...",
    "Back-solving to see what assumptions you *must* have made...",
    "Checking if you rounded at the end or 47 times along the way...",
    "Applying a margin for adverse deviation to your confidence...",
    "Running an experience study on your previous mistakes...",
    "Checking if your explanation is GAAP, IFRS 17, or pure storytelling...",
    "Reviewing your solution like a grumpy grading TA after midnight...",
    "Separating conceptual errors from 'I swear this is a calculator typo'...",
    "Evaluating your answer under the ASOP of Common Sense...",

    "Checking if you actually answered the question asked (plot twist)...",
    "Scanning for magic numbers that appeared with no justification...",
    "Judging whether your shortcut was clever or completely illegal...",
    "Stress testing your logic under extreme but plausible scenarios...",
    "Checking if 'approximately' means 'close enough' or 'not even close'...",
    "Projecting how many points you'd get on an actual exam...",
    "Determining if you overcomplicated a 1-point question (again)...",
    "Looking for any sign you read the last sentence of the problem...",
    "Checking whether your method is valid or just fan fiction...",
    "Running a Monte Carlo simulation on: pass, fail, or 'deferred hope'..."
  ];

  useEffect(() => {
    // Reset all state when question ID changes
    setQuestion(null);
    setAnswer('');
    setSelectedOption(null);
    setOriginalAnswer('');
    setAnalysis(null);
    setSubmissionId(null);
    setError(null);
    setChatMessages([]);
    setChatInput('');
    setActiveTab(0);
    setSelectedSubmission(null);
    setSubmissions([]);
    setIsViewingPastSubmission(false);
    stopRealtimeInterview({ exitAudioMode: true, resetSession: true });
    setLoading(true);
    fetchQuestion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopRealtimeInterview({ resetSession: true });
    };
  }, []);

  useEffect(() => {
    // Scroll to bottom of chat when new messages arrive
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages]);

  useEffect(() => {
    let interval;
    const rotating = submitting || isGradingAudioInterview;
    if (rotating) {
      // Reset used phrases when starting a new submission
      usedPhrasesRef.current.clear();
      const availablePhrases = [...loadingPhrases];

      // Function to get a random unused phrase
      const getRandomPhrase = () => {
        const usedPhrases = usedPhrasesRef.current;

        // If all phrases have been used, reset the pool
        if (usedPhrases.size >= availablePhrases.length) {
          usedPhrases.clear();
        }

        // Get available (unused) phrases by filtering out used indices
        const unusedIndices = availablePhrases
          .map((_, index) => index)
          .filter(index => !usedPhrases.has(index));

        if (unusedIndices.length === 0) {
          // Fallback: reset if somehow all are used
          usedPhrases.clear();
          const randomIndex = Math.floor(Math.random() * availablePhrases.length);
          usedPhrases.add(randomIndex);
          return availablePhrases[randomIndex];
        }

        // Pick a random unused index
        const randomUnusedIndex = unusedIndices[Math.floor(Math.random() * unusedIndices.length)];
        usedPhrases.add(randomUnusedIndex);

        return availablePhrases[randomUnusedIndex];
      };

      // Set initial phrase
      setLoadingPhrase(getRandomPhrase());

      // Rotate phrases every 1.5 seconds with random selection
      interval = setInterval(() => {
        setLoadingPhrase(getRandomPhrase());
      }, 1700);
    } else {
      setLoadingPhrase('');
      // Reset used phrases when submission ends
      usedPhrasesRef.current.clear();
    }

    return () => {
      if (interval) clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitting, isGradingAudioInterview]);

  const fetchQuestion = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/questions/${id}`);
      setQuestion(response.data);
      // Reset audio mode when question changes
      if (audioMode === 'audio') {
        stopRealtimeInterview({ exitAudioMode: true, resetSession: true });
      }
      // Reset audio state
      setIsConnected(false);
    } catch (error) {
      console.error('Error fetching question:', error);
      setError('Failed to load question');
    } finally {
      setLoading(false);
    }
  };

  const startTranscriptFlushTimer = () => {
    if (transcriptFlushTimerRef.current) return;
    transcriptFlushTimerRef.current = setInterval(() => {
      console.log('🌀 Auto-flushing transcript buffer (interval)');
      flushTranscriptBuffer();
    }, 2000);
  };

  const stopTranscriptFlushTimer = () => {
    if (transcriptFlushTimerRef.current) {
      clearInterval(transcriptFlushTimerRef.current);
      transcriptFlushTimerRef.current = null;
    }
  };

  const queueTranscriptEntry = (entry) => {
    if (!entry || typeof entry.content !== 'string') return;
    const trimmed = entry.content.trim();
    if (!trimmed) return;
    console.log('📝 Queue transcript entry', {
      role: entry.role,
      type: entry.type,
      preview: trimmed.slice(0, 60),
      bufferLength: transcriptBufferRef.current.length + 1
    });
    const payload = {
      ...entry,
      content: trimmed,
      timestamp: entry.timestamp || new Date().toISOString(),
      role: entry.role || 'system',
      type: entry.type || 'text',
      source: entry.source || 'realtime-event'
    };
    transcriptBufferRef.current.push(payload);
  };

  const getAiOutputKey = (event) => {
    const responseId = event?.response?.id || event?.response_id || event?.item?.id;
    const outputIndex = typeof event?.output_index === 'number' ? event.output_index : 0;
    if (!responseId && typeof event?.responseId === 'string') {
      return `${event.responseId}-${outputIndex}`;
    }
    if (!responseId) return null;
    return `${responseId}-${outputIndex}`;
  };

  const extractDeltaString = (delta) => {
    if (!delta) return '';
    if (typeof delta === 'string') return delta;
    if (typeof delta === 'object') {
      if (typeof delta.transcript === 'string') return delta.transcript;
      if (typeof delta.text === 'string') return delta.text;
      if (Array.isArray(delta)) {
        return delta.map(item => extractDeltaString(item)).join('');
      }
    }
    return '';
  };

  const appendAiOutputDelta = (event) => {
    const key = getAiOutputKey(event);
    if (!key) return;
    const delta = extractDeltaString(event.delta || event.text || event.content);
    if (!delta) return;
    console.log('🪄 Accumulating AI delta', {
      key,
      deltaPreview: delta.slice(0, 60)
    });
    aiOutputBufferRef.current[key] = (aiOutputBufferRef.current[key] || '') + delta;
  };

  const flushAiOutputBuffer = (key, event) => {
    if (!key || !aiOutputBufferRef.current[key]) return;
    const text = aiOutputBufferRef.current[key].trim();
    console.log('📦 Flushing AI output buffer', {
      key,
      length: text.length
    });
    if (text) {
      queueTranscriptEntry({
        role: 'assistant',
        content: text,
        type: event?.type || 'response.output_text',
        source: 'response.output_text',
        metadata: {
          responseId: event?.response?.id || event?.response_id || null,
          outputIndex: typeof event?.output_index === 'number' ? event.output_index : null
        }
      });
    }
    delete aiOutputBufferRef.current[key];
  };

  const flushAllAiOutputBuffers = (event) => {
    Object.keys(aiOutputBufferRef.current).forEach(bufferKey => {
      flushAiOutputBuffer(bufferKey, event);
    });
  };

  const finalizeAiOutput = (event) => {
    const key = getAiOutputKey(event);
    if (key) {
      flushAiOutputBuffer(key, event);
      return;
    }
    const responseId = event?.response?.id || event?.response_id;
    if (!responseId) return;
    Object.keys(aiOutputBufferRef.current)
      .filter(bufferKey => bufferKey.startsWith(`${responseId}-`))
      .forEach(bufferKey => flushAiOutputBuffer(bufferKey, event));
  };

  const extractTextFromContent = (contentArray = []) => {
    if (!Array.isArray(contentArray)) return '';
    return contentArray
      .map(contentItem => {
        if (!contentItem) return '';
        if (typeof contentItem.text === 'string') {
          return contentItem.text;
        }
        if (typeof contentItem.transcript === 'string') {
          return contentItem.transcript;
        }
        if (typeof contentItem.value === 'string') {
          return contentItem.value;
        }
        return '';
      })
      .filter(Boolean)
      .join(' ')
      .trim();
  };

  const handleRealtimeEventLogging = (event) => {
    try {
      console.log('📨 Realtime event received for logging', event?.type, event);
      if (!event || typeof event !== 'object') return;
      if (event.type === 'conversation.item.created') {
        const role = event.item?.role || 'system';
        const text = extractTextFromContent(event.item?.content);
        if (text) {
          queueTranscriptEntry({
            role,
            content: text,
            type: event.type,
            source: 'conversation.item.created',
            metadata: { itemId: event.item?.id }
          });
        }
      } else if (event.type === 'conversation.item.input_audio_transcription.completed') {
        const text = event.transcript || extractTextFromContent(event.item?.content);
        if (text) {
          queueTranscriptEntry({
            role: 'user',
            content: text,
            type: event.type,
            source: 'transcription',
            metadata: { itemId: event.item?.id }
          });
        }
      } else if (event.type === 'conversation.item.input_text.completed') {
        const text = extractTextFromContent(event.item?.content);
        if (text) {
          queueTranscriptEntry({
            role: event.item?.role || 'user',
            content: text,
            type: event.type,
            source: 'input_text',
            metadata: { itemId: event.item?.id }
          });
        }
      } else if (event.type === 'response.output_text.delta' || event.type === 'response.output_audio_transcript.delta') {
        appendAiOutputDelta(event);
      } else if (
        event.type === 'response.output_text.completed' ||
        event.type === 'response.output_audio_transcript.completed' ||
        event.type === 'response.output_audio_transcript.done' ||
        event.type === 'response.output_audio.done' ||
        event.type === 'response.completed'
      ) {
        finalizeAiOutput(event);
      } else if (event.type === 'conversation.item.truncated') {
        console.warn('⚠️ Conversation item truncated', event);
        flushAllAiOutputBuffers({
          type: 'conversation.item.truncated',
          response_id: event.item_id,
          response: { id: event.item_id }
        });
      }
    } catch (err) {
      console.error('Failed to log realtime event', err);
    }
  };

  const flushTranscriptBuffer = async (force = false) => {
    if (!liveInterviewIdRef.current) {
      console.warn('⚠️ Cannot flush transcript buffer without liveInterviewId');
      return;
    }

    if (transcriptFlushPromiseRef.current) {
      if (force) {
        try {
          await transcriptFlushPromiseRef.current;
        } catch (err) {
          // Previous flush already handled its own error logging
        }
      } else {
        console.log('⏳ Skipping flush - previous flush still in flight');
        return;
      }
    }

    if (transcriptBufferRef.current.length === 0) {
      console.log('ℹ️ Flush skipped - transcript buffer empty');
      return;
    }

    const entries = [...transcriptBufferRef.current];
    transcriptBufferRef.current = [];
    console.log('🚀 Sending transcript batch to backend', {
      interviewId: liveInterviewIdRef.current,
      count: entries.length,
      force
    });

    const flushPromise = axios.post(
      `${API_BASE_URL}/api/realtime/live-session/${liveInterviewIdRef.current}/log`,
      { entries },
      { withCredentials: true }
    ).catch(error => {
      console.error('Error logging transcript entries:', error);
      transcriptBufferRef.current = [...entries, ...transcriptBufferRef.current];
      throw error;
    }).finally(() => {
      transcriptFlushPromiseRef.current = null;
    });

    transcriptFlushPromiseRef.current = flushPromise;
    return flushPromise;
  };

  const ensureLiveInterviewSession = async () => {
    if (liveInterviewIdRef.current) {
      console.log('♻️ Reusing existing live interview session', {
        interviewId: liveInterviewIdRef.current
      });
      startTranscriptFlushTimer();
      return liveInterviewIdRef.current;
    }

    const response = await axios.post(
      `${API_BASE_URL}/api/realtime/live-session`,
      { questionId: id },
      { withCredentials: true }
    );

    liveInterviewIdRef.current = response.data.interviewId;
    console.log('🆕 Created live interview session', {
      interviewId: liveInterviewIdRef.current,
      questionId: id
    });
    transcriptBufferRef.current = [];
    aiOutputBufferRef.current = {};
    startTranscriptFlushTimer();
    return liveInterviewIdRef.current;
  };

  const formatTranscriptForDisplay = (transcript = []) => {
    if (!Array.isArray(transcript)) return '';
    return transcript.map(entry => {
      const timePart = entry.timestamp ? `[${entry.timestamp}] ` : '';
      const speaker = entry.role ? entry.role.toUpperCase() : 'SYSTEM';
      return `${timePart}${speaker}: ${entry.content}`;
    }).join('\n');
  };

  const finalizeLiveInterview = async ({ reason = 'user' } = {}) => {
    if (!liveInterviewIdRef.current) return;

    setIsGradingAudioInterview(true);
    console.log('🧮 Finalizing live interview', {
      interviewId: liveInterviewIdRef.current,
      reason
    });
    try {
      await flushTranscriptBuffer(true);
      if (transcriptFlushPromiseRef.current) {
        try {
          await transcriptFlushPromiseRef.current;
        } catch (err) {
          // Error already logged
        }
      }

      const response = await axios.post(
        `${API_BASE_URL}/api/realtime/live-session/${liveInterviewIdRef.current}/complete`,
        { reason },
        { withCredentials: true }
      );

      const { analysis: interviewAnalysis, transcript, submissionId: returnedSubmissionId } = response.data;
      console.log('📊 Received audio analysis', {
        score: interviewAnalysis?.score,
        transcriptEntries: transcript?.length || 0,
        submissionId: returnedSubmissionId
      });
      if (interviewAnalysis) {
        const transcriptText = formatTranscriptForDisplay(transcript || []);
        setOriginalAnswer(transcriptText);
        setAnalysis(interviewAnalysis);
        setSubmissionId(returnedSubmissionId || null);
        setIsViewingPastSubmission(false);
        setSelectedSubmission(null);

        // Fetch whiteboard history before clearing liveInterviewIdRef
        const currentInterviewId = liveInterviewIdRef.current;
        if (currentInterviewId) {
          fetchWhiteboardHistory(currentInterviewId);
        }

        // Fetch audio URL if submission was created
        if (returnedSubmissionId) {
          fetchAudioUrl(returnedSubmissionId);
        }

        // Initialize chat with analysis summary
        setChatMessages([{
          role: 'assistant',
          content: `I've analyzed your audio interview. You scored ${interviewAnalysis.score}/100. ${interviewAnalysis.feedback}\n\nFeel free to ask me any questions about the problem, your answer, or the concepts involved!`
        }]);
        setChatInput('');

        // Refresh submissions list if on submissions tab
        if (activeTab === 1) {
          fetchSubmissions();
        }

        // Dispatch event to refresh streak in navbar
        window.dispatchEvent(new CustomEvent('submissionCreated'));
      }
      liveInterviewIdRef.current = null;
    } catch (error) {
      console.error('❌ Failed to complete live interview:', error, {
        status: error.response?.status,
        data: error.response?.data
      });
      if (error.response?.status === 400 && error.response?.data?.error === 'Transcript is empty, nothing to grade') {
        setError('No transcript was captured to grade. Please give the AI and your mic a few seconds before ending the interview.');
      } else {
        setError(error.response?.data?.error || 'Failed to grade audio interview.');
      }
    } finally {
      setIsGradingAudioInterview(false);
      stopTranscriptFlushTimer();
      transcriptBufferRef.current = [];
      transcriptFlushPromiseRef.current = null;
      liveInterviewIdRef.current = null;
      aiOutputBufferRef.current = {};
      console.log('🧹 Live interview session cleaned up');
    }
  };

  // Audio recording helper functions
  const setupAudioRecording = async (localStream, remoteStream) => {
    try {
      // Clean up any existing recording setup
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        await audioContextRef.current.close();
      }

      // Create AudioContext for mixing streams
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;

      // Create destination for mixed audio
      const destination = audioContext.createMediaStreamDestination();
      mixedStreamRef.current = destination.stream;

      // Get audio tracks from both streams
      const localTracks = localStream.getAudioTracks();
      const remoteTracks = remoteStream ? remoteStream.getAudioTracks() : [];

      // Create source nodes and connect to destination
      localTracks.forEach(track => {
        const source = audioContext.createMediaStreamSource(new MediaStream([track]));
        source.connect(destination);
      });

      if (remoteTracks.length > 0) {
        remoteTracks.forEach(track => {
          const source = audioContext.createMediaStreamSource(new MediaStream([track]));
          source.connect(destination);
        });
      }

      // Set up MediaRecorder
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : 'audio/ogg;codecs=opus';

      const mediaRecorder = new MediaRecorder(destination.stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      recordedChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        console.log('🎙️ MediaRecorder stopped, chunks:', recordedChunksRef.current.length);
        if (recordedChunksRef.current.length > 0) {
          await uploadAudioRecording();
        }
        // Clean up
        if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
          await audioContextRef.current.close();
          audioContextRef.current = null;
        }
        mixedStreamRef.current = null;
      };

      // Start recording
      mediaRecorder.start(1000); // Collect data every second
      console.log('🎙️ Audio recording started (mixed stream)');
    } catch (error) {
      console.error('❌ Error setting up audio recording:', error);
    }
  };

  const uploadAudioRecording = async () => {
    try {
      if (recordedChunksRef.current.length === 0) {
        console.warn('⚠️ No audio chunks to upload');
        return;
      }

      const blob = new Blob(recordedChunksRef.current, {
        type: mediaRecorderRef.current?.mimeType || 'audio/webm'
      });

      console.log('📤 Uploading audio recording, size:', blob.size, 'bytes');

      if (!liveInterviewIdRef.current) {
        console.warn('⚠️ No live interview ID, cannot upload audio');
        return;
      }

      // Step 1: Get presigned URL from backend
      const contentType = mediaRecorderRef.current?.mimeType || 'audio/webm';
      const presignedResponse = await axios.post(
        `${API_BASE_URL}/api/realtime/presigned-upload-url`,
        {
          interviewId: liveInterviewIdRef.current,
          contentType
        },
        { withCredentials: true }
      );

      const { presignedUrl, s3Key } = presignedResponse.data;
      console.log('📝 Got presigned URL for upload:', s3Key);

      // Step 2: Upload directly to S3 using presigned URL
      const uploadResponse = await fetch(presignedUrl, {
        method: 'PUT',
        body: blob,
        headers: {
          'Content-Type': contentType
        }
      });

      if (!uploadResponse.ok) {
        throw new Error(`S3 upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`);
      }

      console.log('✅ Audio uploaded to S3 directly');

      // Step 3: Confirm upload completion with backend
      const confirmResponse = await axios.post(
        `${API_BASE_URL}/api/realtime/confirm-audio-upload`,
        {
          interviewId: liveInterviewIdRef.current,
          s3Key
        },
        { withCredentials: true }
      );

      console.log('✅ Audio upload confirmed:', confirmResponse.data);
      recordedChunksRef.current = [];
    } catch (error) {
      console.error('❌ Error uploading audio recording:', error);
      // Don't throw - this is a background operation and shouldn't break the UI
    }
  };

  // Realtime audio interview handlers
  const handleAudioModeChange = (event, newMode) => {
    if (isGradingAudioInterview) return;
    if (newMode !== null) {
      setAudioMode(newMode);
      if (newMode === 'audio') {
        startRealtimeInterview();
      } else {
        if (liveInterviewIdRef.current || isConnected) {
          handleEndAudioInterview('toggle_to_text');
        } else {
          stopRealtimeInterview({ exitAudioMode: true, resetSession: true });
        }
      }
    }
  };

  const startRealtimeInterview = async () => {
    console.log('🚀 ===== STARTING REALTIME INTERVIEW (WebRTC Unified Interface) =====');
    console.log('🚀 User:', user?.id);
    console.log('🚀 Question:', question?.questionId);

    if (!user || !question) {
      console.error('❌ Missing user or question');
      setError('You must be logged in and have a question loaded.');
      return;
    }

    if (isConnected || pcRef.current || isGradingAudioInterview) {
      console.warn('Realtime interview already active or grading in progress');
      return;
    }

    try {
      await ensureLiveInterviewSession();

      // 1) Create RTCPeerConnection
      const pc = new RTCPeerConnection();
      pcRef.current = pc;
      console.log('✅ RTCPeerConnection created');

      // 2) Set up remote audio output (AI responses) and audio recording
      if (!remoteAudioRef.current) {
        remoteAudioRef.current = new Audio();
        remoteAudioRef.current.autoplay = true;
        console.log('✅ Audio element created for AI responses');
      }

      const remoteStreamRef = { current: null };
      pc.ontrack = (e) => {
        console.log('🔊 Received remote audio track from AI');
        if (remoteAudioRef.current) {
          remoteAudioRef.current.srcObject = e.streams[0];
        }
        remoteStreamRef.current = e.streams[0];
        // Start audio recording once we have both streams
        if (remoteStreamRef.current) {
          setupAudioRecording(ms, remoteStreamRef.current);
        }
      };

      // 3) Get local microphone and add to peer connection
      const ms = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = ms;
      ms.getTracks().forEach(track => {
        pc.addTrack(track, ms);
        console.log('🎤 Added local audio track:', track.id);
      });

      // 4) Create data channel for sending/receiving events
      const dc = pc.createDataChannel('oai-events');
      dataChannelRef.current = dc;

      dc.onopen = () => {
        console.log('✅ Data channel opened');
      };

      dc.onmessage = (e) => {
        try {
          const event = JSON.parse(e.data);
          console.log('📨 Realtime event:', event.type, event);

          if (event.type === 'session.created') {
            const sessionUpdate = {
              type: 'session.update',
              session: {
                modalities: ['audio', 'text'],
                audio: {
                  input: {
                    transcription: {
                      model: 'gpt-4o-mini-transcribe',
                      language: 'en'
                    }
                  }
                }
              }
            };
            dc.send(JSON.stringify(sessionUpdate));
          }

          handleRealtimeEventLogging(event);
        } catch (err) {
          console.log('📨 Non-JSON data channel message:', e.data);
        }
      };

      dc.onerror = (error) => {
        console.error('❌ Data channel error:', error);
      };

      // 5) Create offer and send SDP to our unified interface endpoint
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      console.log('✅ Created WebRTC offer');

      // Send SDP to backend unified interface
      const sessionResponse = await axios.post(
        `${API_BASE_URL}/api/realtime/session?questionId=${id}`,
        offer.sdp,
        {
          headers: { 'Content-Type': 'application/sdp' },
          withCredentials: true
        }
      );

      const answerSdp = sessionResponse.data;
      console.log('✅ Received SDP answer from backend');

      // 6) Set remote description with answer
      const answer = { type: 'answer', sdp: answerSdp };
      await pc.setRemoteDescription(answer);
      console.log('✅ WebRTC connection established with OpenAI Realtime API');

      setIsConnected(true);
      console.log('🎉 Realtime interview started! Start speaking and the AI will respond.');

    } catch (error) {
      console.error('❌ Error starting realtime interview:', error);
      setError('Failed to start audio interview. Please try again.');
      stopRealtimeInterview({ exitAudioMode: true, resetSession: true });
    }
  };

  const stopRealtimeInterview = ({ exitAudioMode = false, resetSession = false } = {}) => {
    console.log('🛑 Stopping realtime interview');

    // Stop audio recording
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      console.log('🛑 Stopping audio recording...');
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
    }

    // Close data channel
    if (dataChannelRef.current) {
      dataChannelRef.current.close();
      dataChannelRef.current = null;
    }

    // Close peer connection and stop all tracks
    if (pcRef.current) {
      pcRef.current.getSenders().forEach(sender => {
        if (sender.track) {
          sender.track.stop();
        }
      });
      pcRef.current.close();
      pcRef.current = null;
    }

    // Stop local media stream
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }

    // Clear remote audio
    if (remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = null;
    }

    stopTranscriptFlushTimer();
    aiOutputBufferRef.current = {};
    if (liveInterviewIdRef.current) {
      flushTranscriptBuffer().catch(err => {
        console.error('Failed to flush transcript buffer during stop:', err);
      });
    }

    setIsConnected(false);
    if (exitAudioMode) {
      setAudioMode('text');
    }

    if (resetSession) {
      liveInterviewIdRef.current = null;
      transcriptBufferRef.current = [];
      aiOutputBufferRef.current = {};
    }
    console.log('✅ Realtime interview stopped');
  };

  async function handleEndAudioInterview(reason = 'user_requested') {
    if (isGradingAudioInterview) return;
    setAudioMode('text');
    stopRealtimeInterview({ exitAudioMode: false });
    if (liveInterviewIdRef.current) {
      await finalizeLiveInterview({ reason });
    }
  }

  const handleWhiteboardStrokes = async (strokes) => {
    if (!strokes || strokes.length === 0) return;

    // 1. Send to Backend (fire and forget to avoid blocking UI)
    if (liveInterviewIdRef.current) {
      axios.post(
        `${API_BASE_URL}/api/realtime/live-session/${liveInterviewIdRef.current}/whiteboard`,
        { strokes },
        { withCredentials: true }
      ).catch(err => console.error('Failed to log strokes to backend:', err));
    }

    // 2. Send to Realtime API via Data Channel
    if (dataChannelRef.current && dataChannelRef.current.readyState === 'open') {
      // Optimize payload: Compact format and downsampling
      // Format: "S x y" (Start), "M x y" (Move), "E x y" (End)
      // Downsample moves: Keep 1 in 3 to reduce volume

      const compactStrokes = strokes.reduce((acc, stroke, index) => {
        const x = Math.round(stroke.x);
        const y = Math.round(stroke.y);

        if (stroke.type === 'stroke_start') {
          acc.push(`S ${x} ${y}`);
        } else if (stroke.type === 'stroke_end') {
          acc.push(`E ${x} ${y}`);
        } else if (stroke.type === 'stroke_move') {
          // Simple downsampling: only keep if index % 3 === 0 or it's the last one
          if (index % 3 === 0) {
            acc.push(`M ${x} ${y}`);
          }
        }
        return acc;
      }, []);

      if (compactStrokes.length > 0) {
        const payload = {
          type: 'conversation.item.create',
          item: {
            type: 'message',
            role: 'user',
            content: [
              {
                type: 'input_text',
                text: `WHITEBOARD_UPDATE: ${compactStrokes.join('|')}`
              }
            ]
          }
        };

        try {
          dataChannelRef.current.send(JSON.stringify(payload));
          console.log('🎨 Sent optimized whiteboard strokes to Realtime API', compactStrokes.length);
        } catch (err) {
          console.error('Failed to send strokes to Realtime API:', err);
        }
      }
    }
  };

  const handleSubmit = async () => {
    const isMultipleChoice = question?.questionType === 'Multiple Choice' && question?.options && question.options.length > 0;

    // For multiple choice, require selectedOption; for free response, require answer
    if (isMultipleChoice && !selectedOption) {
      setError('Please select an answer option');
      return;
    }
    if (!isMultipleChoice && !answer.trim()) {
      setError('Please provide an answer before submitting');
      return;
    }

    setSubmitting(true);
    setError(null);

    // Store the original answer - for multiple choice, use selected option + work
    const fullAnswer = isMultipleChoice
      ? (answer.trim() ? `Selected: ${selectedOption}\n\nWork/Context:\n${answer}` : `Selected: ${selectedOption}`)
      : answer;
    setOriginalAnswer(fullAnswer);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/submissions`,
        {
          questionId: id,
          answer: fullAnswer,
          selectedOption: isMultipleChoice ? selectedOption : null,
          workText: isMultipleChoice ? answer.trim() || null : null
        },
        { withCredentials: true }
      );
      setAnalysis(response.data.analysis);
      setSubmissionId(response.data.submissionId);
      setIsViewingPastSubmission(false); // This is a new submission, not viewing a past one

      // Initialize chat with analysis summary
      setChatMessages([{
        role: 'assistant',
        content: `I've analyzed your answer. You scored ${response.data.analysis.score}/100. ${response.data.analysis.feedback}\n\nFeel free to ask me any questions about the problem, your answer, or the concepts involved!`
      }]);

      // Refresh submissions list if on submissions tab
      if (activeTab === 1) {
        fetchSubmissions();
      }

      // Dispatch event to refresh streak in navbar
      window.dispatchEvent(new CustomEvent('submissionCreated'));
    } catch (error) {
      console.error('Error submitting answer:', error);
      setError(error.response?.data?.error || 'Failed to submit answer');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChatSubmit = async () => {
    if (!chatInput.trim() || !submissionId) return;

    const userMessage = chatInput.trim();
    setChatInput('');
    setChatLoading(true);

    // Add user message to chat
    const newMessages = [...chatMessages, { role: 'user', content: userMessage }];
    setChatMessages(newMessages);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/submissions/${submissionId}/chat`,
        {
          message: userMessage,
          conversationHistory: [] // Backend will use stored history
        },
        { withCredentials: true }
      );

      // Add AI response to chat
      setChatMessages([...newMessages, { role: 'assistant', content: response.data.response }]);

      // Refresh submissions list if on submissions tab to get updated conversation history
      if (activeTab === 1) {
        setTimeout(async () => {
          await fetchSubmissions();
          // The selected submission will be updated when user clicks it again, or we can update it here
          // For now, the chat messages are already updated in state
        }, 500);
      }
    } catch (error) {
      console.error('Error sending chat message:', error);
      setChatMessages([
        ...newMessages,
        { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' }
      ]);
    } finally {
      setChatLoading(false);
    }
  };

  const fetchSubmissions = async () => {
    if (!user) return;
    setLoadingSubmissions(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/submissions/question/${id}`,
        { withCredentials: true }
      );
      setSubmissions(response.data.submissions || []);
    } catch (error) {
      console.error('Error fetching submissions:', error);
    } finally {
      setLoadingSubmissions(false);
    }
  };

  useEffect(() => {
    if (activeTab === 1 && user) {
      fetchSubmissions();
    } else if (activeTab === 0) {
      // Reset answer/analysis state when switching back to Description tab
      // Only reset if we're viewing a past submission (not a fresh submission)
      if (isViewingPastSubmission) {
        setAnswer('');
        setSelectedOption(null);
        setAnalysis(null);
        setSubmissionId(null);
        setOriginalAnswer('');
        setChatMessages([]);
        setChatInput('');
        setSelectedSubmission(null);
        setIsViewingPastSubmission(false);
        setAudioUrl(null);
        setLoadingAudio(false);
        setWhiteboardHistory([]);
        setLoadingWhiteboard(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, id, user]);

  const fetchAudioUrl = async (submissionId) => {
    if (!submissionId) return;

    setLoadingAudio(true);
    setAudioUrl(null);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/realtime/audio/${submissionId}`,
        { withCredentials: true }
      );
      setAudioUrl(response.data.audioUrl);
    } catch (error) {
      // If no audio exists, that's fine - just don't show the player
      if (error.response?.status !== 404) {
        console.error('Error fetching audio URL:', error);
      }
    } finally {
      setLoadingAudio(false);
    }
  };

  const fetchWhiteboardHistory = async (interviewId) => {
    if (!interviewId) return;
    setLoadingWhiteboard(true);
    setWhiteboardHistory([]);
    try {
      // We need to fetch the interview details to get the whiteboard events
      // Since we don't have a direct endpoint for just events, we'll fetch the interview
      // Note: We might need to add an endpoint for this if the interview object gets too big,
      // but for now we can reuse the existing pattern or add a specific route.
      // Actually, let's add a specific route in the backend or just fetch the interview.
      // Since we don't have a "get interview" endpoint exposed to frontend easily without auth checks matching,
      // let's rely on the fact that we can add a route or use an existing one.
      // Wait, we don't have a "get interview" endpoint for the frontend.
      // Let's add a simple one or just use the submission to link back.
      // Actually, let's add a route to fetch interview details by ID if user owns it.

      const response = await axios.get(
        `${API_BASE_URL}/api/realtime/live-session/${interviewId}`,
        { withCredentials: true }
      );

      if (response.data && response.data.whiteboardEvents) {
        // Flatten the batches of strokes into a single array of events
        const allEvents = response.data.whiteboardEvents.flatMap(batch => batch.strokes || batch);
        setWhiteboardHistory(allEvents);
      }
    } catch (error) {
      console.error('Error fetching whiteboard history:', error);
    } finally {
      setLoadingWhiteboard(false);
    }
  };

  const submissionHasAudio = (submission) => {
    if (!submission) return false;
    return Boolean(submission.liveInterviewId);
  };

  const handleSubmissionClick = (submission) => {
    setSelectedSubmission(submission);
    setSubmissionId(submission.submissionId);
    setAnalysis(submission.analysis);
    setOriginalAnswer(submission.answer);
    setIsViewingPastSubmission(true); // Mark that we're viewing a past submission

    // Fetch audio URL if available
    if (submissionHasAudio(submission)) {
      fetchAudioUrl(submission.submissionId);
      // Also fetch whiteboard history if we have a live interview ID
      const interviewId = submission.liveInterviewId || submission.interviewId;
      if (interviewId) {
        fetchWhiteboardHistory(interviewId);
      }
    } else {
      setAudioUrl(null);
      setLoadingAudio(false);
      setWhiteboardHistory([]);
    }

    // Load conversation history if it exists
    if (submission.conversationHistory && submission.conversationHistory.length > 0) {
      setChatMessages(submission.conversationHistory);
    } else {
      // Initialize with analysis summary if no conversation history
      setChatMessages([{
        role: 'assistant',
        content: `I've analyzed your answer. You scored ${submission.analysis.score}/100. ${submission.analysis.feedback}\n\nFeel free to ask me any questions about the problem, your answer, or the concepts involved!`
      }]);
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Easy': return 'success';
      case 'Medium': return 'warning';
      case 'Hard': return 'error';
      default: return 'default';
    }
  };

  // Memoize question section - must be before early returns
  const questionSection = useMemo(() => {
    if (!question) return null;

    return (
      <Paper elevation={3} sx={{ padding: 3, height: '100%', overflow: 'auto' }}>
        <Button onClick={() => navigate('/')} sx={{ marginBottom: 2 }}>
          ← Back to Questions
        </Button>

        <Box sx={{ display: 'flex', gap: 1, marginBottom: 2, flexWrap: 'wrap' }}>
          <Chip label={question.examType} color="primary" />
          <Chip
            label={question.difficulty}
            color={getDifficultyColor(question.difficulty)}
          />
          <Chip label={question.topic} variant="outlined" />
          <Chip label={question.questionType} variant="outlined" />
        </Box>

        <Typography variant="h4" gutterBottom>
          Question
        </Typography>
        <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-wrap', fontSize: '1.1rem' }}>
          {question.questionText}
        </Typography>

        {question.options && (
          <Box sx={{ marginTop: 2 }}>
            <Typography variant="h6" gutterBottom>Options:</Typography>
            <List>
              {question.options.map((option, index) => (
                <ListItem key={index}>
                  <ListItemText primary={`${String.fromCharCode(65 + index)}. ${option}`} />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

      </Paper>
    );
  }, [question, navigate]);

  // Submissions list view
  const submissionsView = useMemo(() => {
    if (!user) {
      return (
        <Paper elevation={3} sx={{ padding: 3 }}>
          <Alert severity="info">Please log in to view your submission history.</Alert>
        </Paper>
      );
    }

    if (loadingSubmissions) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (submissions.length === 0) {
      return (
        <Paper elevation={3} sx={{ padding: 3 }}>
          <Typography variant="h6" gutterBottom>Submissions</Typography>
          <Alert severity="info">No submissions yet. Submit an answer to see it here!</Alert>
        </Paper>
      );
    }

    const sortedSubmissions = [...submissions].sort((a, b) => {
      const aTime = a?.submittedAt ? new Date(a.submittedAt).getTime() : 0;
      const bTime = b?.submittedAt ? new Date(b.submittedAt).getTime() : 0;
      return bTime - aTime;
    });

    return (
      <Box>
        <Typography variant="h6" gutterBottom sx={{ marginBottom: 2 }}>Submissions</Typography>
        {sortedSubmissions.map((submission, index) => (
          <Card
            key={submission.submissionId}
            sx={{
              marginBottom: 2,
              cursor: 'pointer',
              border: selectedSubmission?.submissionId === submission.submissionId ? 2 : 1,
              borderColor: selectedSubmission?.submissionId === submission.submissionId ? 'primary.main' : 'divider',
              '&:hover': {
                boxShadow: 4
              }
            }}
            onClick={() => handleSubmissionClick(submission)}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 1 }}>
                <Typography variant="h6">Submission #{sortedSubmissions.length - index}</Typography>
                <Chip
                  label={submission.analysis.isCorrect ? 'Accepted' : 'Incorrect'}
                  color={submission.analysis.isCorrect ? 'success' : 'error'}
                  icon={submission.analysis.isCorrect ? <CheckCircleIcon /> : <CancelIcon />}
                />
              </Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {new Date(submission.submittedAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </Typography>
              <Chip
                label={`Score: ${submission.analysis.score}/100`}
                color={submission.analysis.score >= 80 ? 'success' : submission.analysis.score >= 60 ? 'warning' : 'error'}
                size="small"
                sx={{ marginTop: 1 }}
              />
              {submission.conversationHistory && submission.conversationHistory.length > 0 && (
                <Chip
                  label={`${submission.conversationHistory.length} messages`}
                  size="small"
                  variant="outlined"
                  sx={{ marginLeft: 1, marginTop: 1 }}
                />
              )}
            </CardContent>
          </Card>
        ))}
      </Box>
    );
  }, [user, loadingSubmissions, submissions, selectedSubmission]);

  // Answer section - must be before early returns
  const isMultipleChoice = question?.questionType === 'Multiple Choice' && question?.options && question.options.length > 0;
  const isDesignQuestion = question?.questionType === 'Design';

  const answerSection = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 2 }}>
      {!analysis ? (
        <Paper elevation={3} sx={{ padding: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5">
              Your Answer
            </Typography>
            {isDesignQuestion && (
              <ToggleButtonGroup
                value={audioMode}
                exclusive
                onChange={handleAudioModeChange}
                aria-label="answer mode"
                size="small"
              >
                <ToggleButton value="text" aria-label="text mode">
                  Text
                </ToggleButton>
                <ToggleButton value="audio" aria-label="audio mode">
                  <MicIcon sx={{ mr: 1 }} />
                  Audio Interview
                </ToggleButton>
              </ToggleButtonGroup>
            )}
          </Box>

          {audioMode === 'audio' && isDesignQuestion ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Box sx={{ mb: 4, mx: 'auto', maxWidth: 800, height: 400, border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
                <Whiteboard onStrokes={handleWhiteboardStrokes} disabled={!isConnected || isGradingAudioInterview} />
              </Box>
              {isGradingAudioInterview ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  <CircularProgress />
                  <Typography variant="h6" sx={{ maxWidth: 360 }}>
                    {loadingPhrase || 'Summarizing your interview...'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Hang tight while we transcribe and grade your responses.
                  </Typography>
                </Box>
              ) : isConnected ? (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 3 }}>
                    <MicIcon color="primary" sx={{ fontSize: 40 }} />
                    <Typography variant="h6">Connected - Start speaking and the AI will respond!</Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => handleEndAudioInterview('user_requested')}
                    startIcon={<MicOffIcon />}
                  >
                    End Interview
                  </Button>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    The AI interviewer will ask you questions and provide feedback in real-time.
                  </Typography>
                </>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  <CircularProgress />
                  <Typography variant="body2" color="text.secondary">
                    Connecting you to the AI interviewer...
                  </Typography>
                </Box>
              )}
            </Box>
          ) : isMultipleChoice ? (
            <>
              <Typography variant="subtitle1" gutterBottom sx={{ mt: 2, mb: 1 }}>
                Select your answer:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                {question.options.map((option, index) => (
                  <Button
                    key={index}
                    variant={selectedOption === option ? 'contained' : 'outlined'}
                    {...(selectedOption === option && { color: 'primary' })}
                    onClick={() => setSelectedOption(option)}
                    disabled={isProcessingAnswer}
                    sx={{
                      justifyContent: 'flex-start',
                      textTransform: 'none',
                      py: 1.5,
                      px: 2
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <Chip
                        label={String.fromCharCode(65 + index)}
                        size="small"
                        color={selectedOption === option ? 'primary' : 'default'}
                        sx={{ minWidth: 32 }}
                      />
                      <Typography sx={{ flex: 1, textAlign: 'left' }}>{option}</Typography>
                      {selectedOption === option && <CheckCircleIcon />}
                    </Box>
                  </Button>
                ))}
              </Box>
              <TextField
                key="work-input"
                fullWidth
                multiline
                rows={isMobile ? 4 : 6}
                variant="outlined"
                placeholder="Optional: Show your work or add context for the AI to critique..."
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                sx={{ marginBottom: 2 }}
                disabled={isProcessingAnswer}
                helperText="Optional: Add your work or reasoning for AI feedback"
              />
            </>
          ) : (
            <TextField
              key="answer-input"
              fullWidth
              multiline
              rows={isMobile ? 8 : 12}
              variant="outlined"
              placeholder="Write your answer here. For mathematical problems, show your work step by step. For conceptual questions, explain your reasoning."
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              sx={{ marginBottom: 2 }}
              disabled={isProcessingAnswer}
            />
          )}

          <Button
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={isProcessingAnswer || (isMultipleChoice ? !selectedOption : !answer.trim())}
            fullWidth
            startIcon={isProcessingAnswer ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isProcessingAnswer ? loadingPhrase : 'Submit Answer'}
          </Button>
        </Paper>
      ) : (
        <>
          <Paper elevation={3} sx={{ padding: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 2 }}>
              <Typography variant="h5">
                AI Analysis
              </Typography>
              {analysis.isCorrect ? (
                <CheckCircleIcon color="success" fontSize="large" />
              ) : (
                <CancelIcon color="error" fontSize="large" />
              )}
              <Chip
                label={`Score: ${analysis.score}/100`}
                color={analysis.score >= 80 ? 'success' : analysis.score >= 60 ? 'warning' : 'error'}
              />
            </Box>

            <Alert severity={analysis.isCorrect ? 'success' : 'info'} sx={{ marginBottom: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                {analysis.feedback}
              </Typography>
            </Alert>

            {/* Audio Player - show if audio is available (from audio interview) */}
            {audioUrl && (
              <Box sx={{ marginBottom: 2, padding: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 1 }}>
                  <VolumeUpIcon color="primary" />
                  <Typography variant="h6">Interview Recording</Typography>
                </Box>
                {loadingAudio ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">
                      Loading audio...
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <audio
                      controls
                      style={{ width: '100%', marginTop: 1 }}
                      src={audioUrl}
                    >
                      Your browser does not support the audio element.
                    </audio>
                  </Box>
                )}
              </Box>
            )}

            {/* Whiteboard History - show if available or debug */}
            {(whiteboardHistory.length > 0 || isDesignQuestion || loadingWhiteboard) && (
              <Box sx={{ marginBottom: 2, padding: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom>Whiteboard Session</Typography>
                <Box sx={{ height: 400, border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden', bgcolor: 'white' }}>
                  {loadingWhiteboard ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                      <CircularProgress />
                    </Box>
                  ) : whiteboardHistory.length > 0 ? (
                    <Whiteboard
                      disabled={true}
                      initialStrokes={whiteboardHistory}
                    />
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'text.secondary' }}>
                      <Typography>No whiteboard drawing recorded for this session.</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Your Original Answer</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ padding: 2, backgroundColor: '#f9f9f9', borderRadius: 1 }}>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                    {originalAnswer || answer}
                  </Typography>
                </Box>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Detailed Analysis</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {analysis.detailedAnalysis}
                </Typography>
              </AccordionDetails>
            </Accordion>

            {analysis.strengths && analysis.strengths.length > 0 && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">Strengths</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List>
                    {analysis.strengths.map((strength, index) => (
                      <ListItem key={index}>
                        <ListItemText primary={`✓ ${strength}`} />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            )}

            {analysis.weaknesses && analysis.weaknesses.length > 0 && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">Areas for Improvement</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List>
                    {analysis.weaknesses.map((weakness, index) => (
                      <ListItem key={index}>
                        <ListItemText primary={`⚠ ${weakness}`} />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            )}

            {analysis.correctApproach && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">Correct Approach</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {analysis.correctApproach}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            )}
          </Paper>

          <Paper elevation={3} sx={{ padding: 2, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 400 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SmartToyIcon /> Ask Questions
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 2 }}>
              Have questions about the problem or feedback? Ask me anything!
            </Typography>

            <Box sx={{ flex: 1, overflow: 'auto', marginBottom: 2, padding: 1, backgroundColor: '#f9f9f9', borderRadius: 1 }}>
              {chatMessages.map((msg, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    gap: 1,
                    marginBottom: 2,
                    flexDirection: msg.role === 'user' ? 'row-reverse' : 'row'
                  }}
                >
                  <Avatar sx={{ bgcolor: msg.role === 'user' ? 'primary.main' : 'secondary.main' }}>
                    {msg.role === 'user' ? <PersonIcon /> : <SmartToyIcon />}
                  </Avatar>
                  <Box
                    sx={{
                      flex: 1,
                      padding: 1.5,
                      borderRadius: 2,
                      backgroundColor: msg.role === 'user' ? 'primary.light' : 'grey.200',
                      maxWidth: '80%'
                    }}
                  >
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {msg.content}
                    </Typography>
                  </Box>
                </Box>
              ))}
              {chatLoading && (
                <Box sx={{ display: 'flex', gap: 1, marginBottom: 2 }}>
                  <Avatar sx={{ bgcolor: 'secondary.main' }}>
                    <SmartToyIcon />
                  </Avatar>
                  <Box sx={{ padding: 1.5, borderRadius: 2, backgroundColor: 'grey.200' }}>
                    <CircularProgress size={20} />
                  </Box>
                </Box>
              )}
              <div ref={chatEndRef} />
            </Box>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                key="chat-input"
                fullWidth
                size="small"
                placeholder="Ask a question..."
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleChatSubmit();
                  }
                }}
                disabled={chatLoading || !submissionId}
              />
              <IconButton
                color="primary"
                onClick={handleChatSubmit}
                disabled={!chatInput.trim() || chatLoading || !submissionId}
              >
                <SendIcon />
              </IconButton>
            </Box>
          </Paper>
        </>
      )}
    </Box>
  );

  // Early returns after all hooks
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ marginTop: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!question) {
    return (
      <Container maxWidth="lg" sx={{ marginTop: 4 }}>
        <Alert severity="error">Question not found</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ marginTop: 2, marginBottom: 4 }}>
      {error && (
        <Alert severity="error" sx={{ marginBottom: 2 }}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Paper elevation={1} sx={{ marginBottom: 2 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab icon={<DescriptionIcon />} iconPosition="start" label="Description" />
          {user && <Tab icon={<HistoryIcon />} iconPosition="start" label="Submissions" />}
        </Tabs>
      </Paper>

      {activeTab === 0 ? (
        // Description/Answer tab
        isMobile ? (
          // Mobile: Stack vertically
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {questionSection}
            {answerSection}
          </Box>
        ) : (
          // Desktop: Split layout
          <Grid container spacing={3} sx={{ height: 'calc(100vh - 180px)' }}>
            <Grid item xs={12} md={5}>
              {questionSection}
            </Grid>
            <Grid item xs={12} md={7}>
              {answerSection}
            </Grid>
          </Grid>
        )
      ) : (
        // Submissions tab
        <Grid container spacing={3}>
          <Grid item xs={12} md={selectedSubmission ? 4 : 12}>
            {submissionsView}
          </Grid>
          {selectedSubmission && (
            <Grid item xs={12} md={8}>
              <Paper elevation={3} sx={{ padding: 3, height: '100%', overflow: 'auto' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                  <Typography variant="h5">Submission Details</Typography>
                  <Button onClick={() => {
                    setSelectedSubmission(null);
                    setAudioUrl(null);
                    setLoadingAudio(false);
                    setWhiteboardHistory([]);
                    setLoadingWhiteboard(false);
                  }} size="small">
                    Close
                  </Button>
                </Box>

                <Divider sx={{ marginBottom: 2 }} />

                {/* Audio Player */}
                {(audioUrl || submissionHasAudio(selectedSubmission)) && (
                  <Box sx={{ marginBottom: 3, padding: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 1 }}>
                      <VolumeUpIcon color="primary" />
                      <Typography variant="h6">Interview Recording</Typography>
                    </Box>
                    {loadingAudio ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <CircularProgress size={20} />
                        <Typography variant="body2" color="text.secondary">
                          Loading audio...
                        </Typography>
                      </Box>
                    ) : audioUrl ? (
                      <Box>
                        <audio
                          controls
                          style={{ width: '100%', marginTop: 1 }}
                          src={audioUrl}
                        >
                          Your browser does not support the audio element.
                        </audio>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Audio recording available. Click to load.
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Whiteboard History - show if available */}
                {(whiteboardHistory.length > 0 || isDesignQuestion || loadingWhiteboard) && (
                  <Box sx={{ marginBottom: 3, padding: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                    <Typography variant="h6" gutterBottom>Whiteboard Session</Typography>
                    <Box sx={{ height: 400, border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden', bgcolor: 'white' }}>
                      {loadingWhiteboard ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                          <CircularProgress />
                        </Box>
                      ) : whiteboardHistory.length > 0 ? (
                        <Whiteboard
                          disabled={true}
                          initialStrokes={whiteboardHistory}
                        />
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'text.secondary' }}>
                          <Typography>No whiteboard drawing recorded for this session.</Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}

                <Box sx={{ marginBottom: 3 }}>
                  <Typography variant="h6" gutterBottom>Your Answer</Typography>
                  <Box sx={{ padding: 2, backgroundColor: '#f9f9f9', borderRadius: 1 }}>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                      {selectedSubmission.answer}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ marginBottom: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 2 }}>
                    <Typography variant="h6">AI Analysis</Typography>
                    {selectedSubmission.analysis.isCorrect ? (
                      <CheckCircleIcon color="success" fontSize="large" />
                    ) : (
                      <CancelIcon color="error" fontSize="large" />
                    )}
                    <Chip
                      label={`Score: ${selectedSubmission.analysis.score}/100`}
                      color={selectedSubmission.analysis.score >= 80 ? 'success' : selectedSubmission.analysis.score >= 60 ? 'warning' : 'error'}
                    />
                  </Box>

                  <Alert severity={selectedSubmission.analysis.isCorrect ? 'success' : 'info'} sx={{ marginBottom: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {selectedSubmission.analysis.feedback}
                    </Typography>
                  </Alert>

                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="h6">Detailed Analysis</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                        {selectedSubmission.analysis.detailedAnalysis}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>

                  {selectedSubmission.analysis.strengths && selectedSubmission.analysis.strengths.length > 0 && (
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6">Strengths</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <List>
                          {selectedSubmission.analysis.strengths.map((strength, index) => (
                            <ListItem key={index}>
                              <ListItemText primary={`✓ ${strength}`} />
                            </ListItem>
                          ))}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  )}

                  {selectedSubmission.analysis.weaknesses && selectedSubmission.analysis.weaknesses.length > 0 && (
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6">Areas for Improvement</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <List>
                          {selectedSubmission.analysis.weaknesses.map((weakness, index) => (
                            <ListItem key={index}>
                              <ListItemText primary={`⚠ ${weakness}`} />
                            </ListItem>
                          ))}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  )}

                  {selectedSubmission.analysis.correctApproach && (
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6">Correct Approach</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                          {selectedSubmission.analysis.correctApproach}
                        </Typography>
                      </AccordionDetails>
                    </Accordion>
                  )}
                </Box>

                <Box>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, marginTop: 3 }}>
                    <SmartToyIcon /> Conversation
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 2 }}>
                    {selectedSubmission.conversationHistory && selectedSubmission.conversationHistory.length > 0
                      ? 'Continue the conversation or review past messages.'
                      : 'Have questions about the problem or feedback? Ask me anything!'}
                  </Typography>

                  <Box sx={{ flex: 1, overflow: 'auto', marginBottom: 2, padding: 1, backgroundColor: '#f9f9f9', borderRadius: 1, minHeight: 300, maxHeight: 400 }}>
                    {chatMessages.map((msg, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          gap: 1,
                          marginBottom: 2,
                          flexDirection: msg.role === 'user' ? 'row-reverse' : 'row'
                        }}
                      >
                        <Avatar sx={{ bgcolor: msg.role === 'user' ? 'primary.main' : 'secondary.main' }}>
                          {msg.role === 'user' ? <PersonIcon /> : <SmartToyIcon />}
                        </Avatar>
                        <Box
                          sx={{
                            flex: 1,
                            padding: 1.5,
                            borderRadius: 2,
                            backgroundColor: msg.role === 'user' ? 'primary.light' : 'grey.200',
                            maxWidth: '80%'
                          }}
                        >
                          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                            {msg.content}
                          </Typography>
                          {msg.timestamp && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', marginTop: 0.5 }}>
                              {new Date(msg.timestamp).toLocaleString()}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ))}
                    {chatLoading && (
                      <Box sx={{ display: 'flex', gap: 1, marginBottom: 2 }}>
                        <Avatar sx={{ bgcolor: 'secondary.main' }}>
                          <SmartToyIcon />
                        </Avatar>
                        <Box sx={{ padding: 1.5, borderRadius: 2, backgroundColor: 'grey.200' }}>
                          <CircularProgress size={20} />
                        </Box>
                      </Box>
                    )}
                    <div ref={chatEndRef} />
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Ask a question..."
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleChatSubmit();
                        }
                      }}
                      disabled={chatLoading || !submissionId}
                    />
                    <IconButton
                      color="primary"
                      onClick={handleChatSubmit}
                      disabled={!chatInput.trim() || chatLoading || !submissionId}
                    >
                      <SendIcon />
                    </IconButton>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          )}
        </Grid>
      )}
    </Container>
  );
};

export default QuestionDetail;
